/**
 * Shared "Format Document" keybinding for CodeMirror JSON editors.
 *
 * Triggered by Shift+Alt+F (matching VS Code's default Format Document
 * shortcut). Parses the current document as JSON and rewrites it as
 * pretty-printed JSON (2-space indent). If the document is not valid JSON
 * the command is a no-op.
 */

import type { KeyBinding } from '@codemirror/view';
import type { EditorView } from '@codemirror/view';

/**
 * Format the current document as pretty-printed JSON.
 * Returns true if the document was reformatted, false otherwise.
 */
export const formatJsonDocument = (view: EditorView): boolean => {
  const current = view.state.doc.toString();
  if (!current.trim()) return false;

  let parsed: unknown;
  try {
    parsed = JSON.parse(current);
  } catch {
    // Invalid JSON — silently no-op rather than corrupting the user's content.
    return false;
  }

  const formatted = JSON.stringify(parsed, null, 2);
  if (formatted === current) return true; // already formatted; swallow the keypress

  view.dispatch({
    changes: { from: 0, to: view.state.doc.length, insert: formatted },
    // Preserve cursor position by clamping to new document length.
    selection: {
      anchor: Math.min(view.state.selection.main.anchor, formatted.length),
      head: Math.min(view.state.selection.main.head, formatted.length),
    },
    scrollIntoView: true,
  });
  return true;
};

/** Keymap entry for Shift+Alt+F → format JSON document. */
export const formatJsonKeymap: KeyBinding[] = [
  {
    key: 'Shift-Alt-f',
    preventDefault: true,
    run: formatJsonDocument,
  },
];
